import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { createStorage } from '@matix/utils';

/**
 * App-wide language context for the cashier (EN / AR + RTL).
 *
 * The active language flips `<html lang/dir/data-lang>` and is persisted per
 * terminal in the same store + key the login screen already used
 * (`pos:cashier` → `login_lang`), so the choice on the login screen and the
 * choice inside the app are one and the same.
 *
 * UI strings are inline `t({ en, ar })` at each call site (like the admin
 * app) rather than a message-catalog — keeps translations next to the markup.
 */

export type Lang = 'en' | 'ar';

interface LanguageContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  toggle: () => void;
  t: <T>(opts: { en: T; ar: T }) => T;
  dir: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const STORE = createStorage('pos:cashier');
const LANG_KEY = 'login_lang';

function loadInitial(): Lang {
  return STORE.get<string>(LANG_KEY) === 'ar' ? 'ar' : 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }): JSX.Element {
  const [lang, setLangState] = useState<Lang>(() => loadInitial());

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('lang', lang);
    html.setAttribute('data-lang', lang);
    html.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
    STORE.set(LANG_KEY, lang);
  }, [lang]);

  const setLang = useCallback((l: Lang) => setLangState(l), []);
  const toggle = useCallback(() => setLangState((l) => (l === 'ar' ? 'en' : 'ar')), []);
  const t = useCallback(
    <T,>(opts: { en: T; ar: T }) => (lang === 'ar' ? opts.ar : opts.en),
    [lang],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, setLang, toggle, t, dir: lang === 'ar' ? 'rtl' : 'ltr' }),
    [lang, setLang, toggle, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used inside <LanguageProvider>');
  return ctx;
}

/**
 * Pick the session language's string OUTSIDE React — for code on the order
 * path (a confirm, a message) that runs in a click handler or a mutation.
 * Reads the attribute LanguageProvider keeps on <html>.
 */
export function trNow<T>(opts: { en: T; ar: T }): T {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('data-lang') === 'ar'
    ? opts.ar
    : opts.en;
}
